import React, { useEffect, useState, useCallback } from 'react';
import { Box, Grid, Alert, Typography, Card, CardContent } from '@mui/material';
import DashboardStats from '../components/dashboard/DashboardStats';
import LeadsByStatusChart from '../components/dashboard/LeadsByStatusChart';
import BankWiseChart from '../components/dashboard/BankWiseChart';
import MonthlyTrendChart from '../components/dashboard/MonthlyTrendChart';
import PerformanceTable from '../components/dashboard/PerformanceTable';
import PageHeader from '../components/common/PageHeader';
import LoadingSpinner from '../components/common/LoadingSpinner';
import usePermissions from '../hooks/usePermissions';
import * as dashboardService from '../services/dashboardService';

const DashboardPage = () => {
  const { isAdmin, isManager, canViewDashboardFull } = usePermissions();

  const [stats, setStats] = useState(null);
  const [statusDistribution, setStatusDistribution] = useState([]);
  const [bankWise, setBankWise] = useState([]);
  const [monthlyTrends, setMonthlyTrends] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const statsRes = await dashboardService.getStats();
      setStats(statsRes.data?.data || statsRes.data);

      if (canViewDashboardFull) {
        const [statusRes, bankRes, trendRes] = await Promise.all([
          dashboardService.getStatusDistribution(),
          dashboardService.getBankWise(),
          dashboardService.getMonthlyTrends(new Date().getFullYear()),
        ]);
        setStatusDistribution(statusRes.data?.data?.distribution || []);
        setBankWise(bankRes.data?.data?.bankStats || []);
        setMonthlyTrends(trendRes.data?.data?.trends || []);
      }

      if (isAdmin) {
        const perfRes = await dashboardService.getPerformance();
        setPerformance(perfRes.data?.data?.managerPerformance || []);
      }
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || 'Failed to load dashboard data';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [canViewDashboardFull, isAdmin]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box>
      <PageHeader title="Dashboard" breadcrumbs={[{ label: 'Dashboard' }]} />

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 1 }}>
          {error}
        </Alert>
      )}

      {stats && (
        <Box sx={{ mb: 3 }}>
          <DashboardStats stats={stats} />
        </Box>
      )}

      {canViewDashboardFull ? (
        <>
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <LeadsByStatusChart data={statusDistribution} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <BankWiseChart data={bankWise} />
            </Grid>
          </Grid>

          <Box sx={{ mb: 3 }}>
            <MonthlyTrendChart data={monthlyTrends} />
          </Box>
        </>
      ) : (
        <Card sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography sx={{ fontSize: '1rem', fontWeight: 500, color: '#202124', mb: 0.5 }}>
              Welcome
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: '#5F6368' }}>
              Use the navigation above to manage your assigned students and track their progress.
            </Typography>
          </CardContent>
        </Card>
      )}

      {isAdmin && performance.length > 0 && (
        <PerformanceTable data={performance} />
      )}
    </Box>
  );
};

export default DashboardPage;
